import { inject, Injectable } from '@angular/core';
import { ServiceType } from '@common/enums';
import { AuthUser, Claim, SystemInfo } from '@common/models';
import { AuthStateService } from '@common/services/auth/auth-state.service';
import { AuthService } from '@common/services/auth/auth.service';
import { StoragesService } from '@common/services/storages.service';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class AuthEffectService {
  private readonly authService = inject(AuthService);
  private readonly authState = inject(AuthStateService);
  private readonly storagesService = inject(StoragesService);

  private readonly availableServices = this.authState.availableServices;
  private readonly authUser = this.authState.authUser;
  private readonly claims = this.authState.claims;
  private readonly systemInfo = this.authState.systemInfo;

  getSystemInfo(forceUpdate = false): Observable<SystemInfo> {
    if (this.systemInfo() && !forceUpdate) return of(this.systemInfo());

    return this.authService.getSystemInfo().pipe(tap((info) => this.authState.patchField('systemInfo', info)));
  }

  getAuthUser(forceUpdate = false): Observable<AuthUser> {
    if (this.authUser() && !forceUpdate) return of(this.authUser());

    return this.authService.getAuthUser().pipe(tap((user) => this.authState.patchField('authUser', user)));
  }

  getClaims(): Observable<Claim[]> {
    if (this.claims()) return of(this.claims());

    return this.authService.getClaims().pipe(tap((value) => this.authState.patchField('claims', value ?? [])));
  }

  getAvailableServices(): Observable<ServiceType[]> {
    if (this.availableServices()) return of(this.availableServices());

    return this.authService.getAvailableServices().pipe(tap((services) => this.authState.patchField('availableServices', services ?? [])));
  }

  checkDefaultObjectLock(): Observable<number | null> {
    return this.storagesService.checkDefaultObjectLock();
  }
}
